import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { supabaseSelectWithRetry } from '@/lib/supabaseRetry'; // 🔥 PHASE 3.9 : Ajout retry
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { logError, logInfo } from '@/lib/productionLogger';
import { globalCache } from '@/utils/cache';

export interface ConnectionRequest {
  id: string;
  requester_id: string;
  requested_id: string;
  message?: string;
  status: 'pending' | 'accepted' | 'declined';
  created_at: string;
  updated_at: string;
  requester_profile?: {
    firstname: string;
    lastname: string;
    avatar_url?: string;
  };
  requested_profile?: {
    firstname: string;
    lastname: string;
    avatar_url?: string;
  };
}

export interface Connection {
  id: string;
  firstname: string;
  lastname: string;
  avatar_url?: string;
  company?: string;
  role?: string;
  messageCount?: number;
}

export interface Message {
  id: string;
  sender_id: string;
  recipient_id: string;
  subject?: string;
  content: string;
  is_read: boolean;
  parent_message_id?: string;
  created_at: string;
  updated_at: string;
  message_type: string;
  shared_resource_type?: string;
  shared_resource_id?: string;
  shared_resource_data?: any;
  sender_profile?: {
    firstname: string;
    lastname: string;
    avatar_url?: string;
  };
  recipient_profile?: {
    firstname: string;
    lastname: string;
    avatar_url?: string;
  };
  unreadCount?: number; // Optimized field for count-only queries
}

export const useNetworkingOptimized = ({ enablePolling = false }: { enablePolling?: boolean } = {}) => {
  const { user } = useAuth();
  const [connectionRequests, setConnectionRequests] = useState<ConnectionRequest[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Throttle ref for fetchMessages
  const fetchMessagesTimerRef = useRef<NodeJS.Timeout>();
  const lastMessagesFetchRef = useRef(0);

  const fetchConnectionRequests = useCallback(async () => {
    if (!user) return;

    const cacheKey = `connection_requests_${user.id}`;
    const cached = globalCache.get<ConnectionRequest[]>(cacheKey);
    
    if (cached) {
      setConnectionRequests(cached);
      return;
    }

    try {
      // First fetch connection requests
      const { data: requestsData, error: requestsError } = await supabase
        .from('connection_requests')
        .select('*')
        .or(`requester_id.eq.${user.id},requested_id.eq.${user.id}`)
        .order('created_at', { ascending: false });

      if (requestsError) throw requestsError;

      if (!requestsData || requestsData.length === 0) {
        setConnectionRequests([]);
        globalCache.set(cacheKey, [], 60000); // 1 minute cache for empty results
        return;
      }

      // Get unique user IDs that need profile data
      const userIds = Array.from(new Set([
        ...requestsData.map(req => req.requester_id),
        ...requestsData.map(req => req.requested_id)
      ]));

      // Fetch profile data for all relevant users
      const { data: profilesData, error: profilesError } = await supabase
        .from('club_members')
        .select('user_id, firstname, lastname, image')
        .in('user_id', userIds);

      if (profilesError) throw profilesError;

      // Transform the data to match our interface
      const transformedData = requestsData.map(request => {
        const requesterProfile = profilesData?.find(p => p.user_id === request.requester_id);
        const requestedProfile = profilesData?.find(p => p.user_id === request.requested_id);

        return {
          ...request,
          requester_profile: requesterProfile ? {
            firstname: requesterProfile.firstname || '',
            lastname: requesterProfile.lastname || '',
            avatar_url: requesterProfile.image || ''
          } : undefined,
          requested_profile: requestedProfile ? {
            firstname: requestedProfile.firstname || '',
            lastname: requestedProfile.lastname || '',
            avatar_url: requestedProfile.image || ''
          } : undefined
        };
      });

      setConnectionRequests(transformedData as ConnectionRequest[]);
      globalCache.set(cacheKey, transformedData as ConnectionRequest[], 300000); // 5 minutes cache (optimized)
    } catch (error) {
      console.error('Error fetching connection requests:', error);
    }
  }, [user]);

  const fetchMessages = useCallback(async (bypassCache: boolean = false) => {
    if (!user) return;

    // Throttle: prevent calls more frequent than 1 second
    const now = Date.now();
    if (now - lastMessagesFetchRef.current < 1000 && !bypassCache) {
      console.log('⏭️ fetchMessages throttled - too frequent');
      return;
    }

    const cacheKey = `messages_${user.id}`;
    const cached = globalCache.get<Message[]>(cacheKey);
    
    if (cached && !bypassCache) {
      setMessages(cached);
      return;
    }

    // Clear any pending timer
    if (fetchMessagesTimerRef.current) {
      clearTimeout(fetchMessagesTimerRef.current);
    }

    // Debounce the actual fetch
    fetchMessagesTimerRef.current = setTimeout(async () => {
      lastMessagesFetchRef.current = Date.now();
      
      try {
        console.log('🔍 [useNetworkingOptimized] Chargement des messages optimisés avec retry pour:', user.id);
        
        // PHASE 1 OPTIMIZATION: Add limit to prevent full table scan + retry
        const { data: messagesData, error } = await supabaseSelectWithRetry(
          () => supabase
            .from('messages')
            .select('id, sender_id, recipient_id, content, subject, is_read, created_at, updated_at, message_type, parent_message_id, shared_resource_type, shared_resource_id, shared_resource_data')
            .or(`sender_id.eq.${user.id},recipient_id.eq.${user.id}`)
            .order('created_at', { ascending: false })
            .limit(100)
            .range(0, 99)
        );

        if (error) throw error;
        
        if (messagesData) {
          console.log('✅ [useNetworkingOptimized] Messages chargés:', messagesData.length);
          
          // Valider les données avant de les stocker
          const validMessages = messagesData.filter(msg => {
            const isValid = msg.id && msg.sender_id && msg.recipient_id && msg.created_at;
            if (!isValid) {
              console.warn('⚠️ Message invalide filtré:', msg);
            }
            return isValid;
          });
          
          setMessages(validMessages);
          globalCache.set(cacheKey, validMessages, 60000); // 1 minute cache
          setRefreshTrigger(prev => prev + 1);
        } else {
          setMessages([]);
          globalCache.set(cacheKey, [], 60000);
        }
      } catch (error) {
        console.error('❌ [useNetworkingOptimized] Erreur chargement messages:', error);
        logError(error, { context: 'fetchMessages' });
      }
    }, 300); // Réduit à 300ms pour plus de réactivité
  }, [user]);

  const fetchConnections = useCallback(async () => {
    if (!user) return;

    const cacheKey = `connections_${user.id}`;
    const cached = globalCache.get<Connection[]>(cacheKey);
    
    if (cached) {
      setConnections(cached);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('connection_requests')
        .select('requester_id, requested_id')
        .eq('status', 'accepted')
        .or(`requester_id.eq.${user.id},requested_id.eq.${user.id}`);

      if (error) throw error;
      
      // Déduplication des IDs d'utilisateurs connectés
      const connectedUserIds = Array.from(new Set(
        data?.map(connection => 
          connection.requester_id === user.id ? connection.requested_id : connection.requester_id
        ) || []
      ));

      // Fetch enriched profile data for connected users
      // 🎯 PHASE 2: Corriger les requêtes avec undefined
      const validUserIds = connectedUserIds.filter(id => id != null);
      
      if (validUserIds.length > 0) {
        const { data: profileData, error: profileError } = await supabase
          .from('club_members')
          .select('user_id, firstname, lastname, name, company, role, image')
          .in('user_id', validUserIds);

        if (profileError) throw profileError;

        // Simplified connections without heavy message counting
        const enrichedConnections: Connection[] = connectedUserIds.map((userId) => {
          const profile = profileData?.find(p => p.user_id === userId);
          
          return {
            id: userId,
            firstname: profile?.firstname || profile?.name?.split(' ')[0] || 'Prénom',
            lastname: profile?.lastname || profile?.name?.split(' ').slice(1).join(' ') || 'Nom',
            avatar_url: profile?.image,
            company: profile?.company,
            role: profile?.role,
            messageCount: 0 // Defer message counting to avoid performance hit
          };
        });

        setConnections(enrichedConnections);
        globalCache.set(cacheKey, enrichedConnections, 180000); // 3 minutes cache
      } else {
        setConnections([]);
        globalCache.set(cacheKey, [], 60000); // 1 minute cache for empty results
      }
    } catch (error) {
      console.error('Error fetching connections:', error);
    }
  }, [user]);

  const sendConnectionRequest = useCallback(async (target_user_id: string, message?: string) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('connection_requests')
        .insert({
          requester_id: user.id,
          requested_id: target_user_id,
          message
        });

      if (error) throw error;

      // Invalidate cache
      globalCache.invalidate(`connection_requests_${user.id}`);

      toast.success('Demande de connexion envoyée');
      await fetchConnectionRequests();
      return true;
    } catch (error) {
      console.error('Error sending connection request:', error);
      return false;
    }
  }, [user, fetchConnectionRequests]);

  const respondToConnectionRequest = useCallback(async (requestId: string, status: 'accepted' | 'declined') => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('connection_requests')
        .update({ status })
        .eq('id', requestId)
        .eq('requested_id', user.id);

      if (error) throw error;

      // Invalidate relevant caches
      globalCache.invalidate(`connection_requests_${user.id}`);
      globalCache.invalidate(`connections_${user.id}`);

      toast.success(
        status === 'accepted' 
          ? 'Demande de connexion acceptée' 
          : 'Demande de connexion déclinée'
      );
      
      await fetchConnectionRequests();
      await fetchConnections();
      return true;
    } catch (error) {
      console.error('Error responding to connection request:', error);
      return false;
    }
  }, [user, fetchConnectionRequests, fetchConnections]);

  const sendMessage = useCallback(async (
    recipient_id: string, 
    content: string, 
    subject?: string, 
    parentMessageId?: string,
    messageType: string = 'text',
    sharedResourceType?: string,
    sharedResourceId?: string,
    sharedResourceData?: any
  ) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('messages')
        .insert({
          sender_id: user.id,
          recipient_id: recipient_id,
          content,
          subject,
          parent_message_id: parentMessageId,
          message_type: messageType,
          shared_resource_type: sharedResourceType,
          shared_resource_id: sharedResourceId,
          shared_resource_data: sharedResourceData
        });

      if (error) throw error;

      // Invalidate cache
      globalCache.invalidate(`messages_${user.id}`);

      toast.success('Message envoyé');
      await fetchMessages();
      return true;
    } catch (error) {
      console.error('Error sending message:', error);
      return false;
    }
  }, [user, fetchMessages]);

  const markMessageAsRead = useCallback(async (messageId: string) => {
    if (!user) return false;
    
    try {
      const { error } = await supabase
        .from('messages')
        .update({ is_read: true })
        .eq('id', messageId);
      
      if (error) throw error;
      
      // Force immediate UI update
      setMessages(prev => prev.map(msg => 
        msg.id === messageId ? { ...msg, is_read: true } : msg
      ));
      
      // Invalidate cache
      globalCache.invalidate(`messages_${user.id}`);
      
      return true;
    } catch (error) {
      console.error('Error marking message as read:', error);
      return false;
    }
  }, [user, fetchMessages]);

  const deleteMessage = useCallback(async (messageId: string) => {
    if (!user) return false;
    
    try {
      const { error } = await supabase
        .from('messages')
        .delete()
        .eq('id', messageId);
      
      if (error) throw error;
      
      globalCache.invalidate(`messages_${user.id}`);
      toast.success('Message supprimé');
      
      await fetchMessages();
      return true;
    } catch (error) {
      console.error('Error deleting message:', error);
      return false;
    }
  }, [user, fetchMessages]);

  const deleteConversation = useCallback(async (participantId: string) => {
    if (!user) return false;
    
    try {
      const { error } = await supabase
        .from('messages')
        .delete()
        .or(`and(sender_id.eq.${user.id},recipient_id.eq.${participantId}),and(sender_id.eq.${participantId},recipient_id.eq.${user.id})`);
      
      if (error) throw error;
      
      globalCache.invalidate(`messages_${user.id}`);
      toast.success('Conversation supprimée');
      
      await fetchMessages();
      return true;
    } catch (error) {
      console.error('Error deleting conversation:', error);
      return false;
    }
  }, [user, fetchMessages]);

  // Memoized networking stats - Optimized to use count directly
  const networkingStats = useMemo(() => {
    const pendingRequests = connectionRequests.filter(
      req => req.status === 'pending' && req.requested_id === user?.id
    ).length;
    
    // Extract unread count from optimized messages structure
    const unreadMessages = messages[0]?.unreadCount || 0;

    const sentPendingRequests = connectionRequests.filter(
      req => req.status === 'pending' && req.requester_id === user?.id
    ).length;

    return {
      totalConnections: connections.length,
      pendingRequests,
      sentPendingRequests,
      unreadMessages,
      totalMessages: 0 // No longer tracking total as we only count unread
    };
  }, [connectionRequests, messages, connections, user?.id, refreshTrigger]);

  useEffect(() => {
    if (user) {
      // Progressive loading: critical data first
      const loadData = async () => {
        setLoading(true);
        try {
          // Load in parallel with Promise.allSettled for better performance
          const results = await Promise.allSettled([
            fetchConnectionRequests(),
            fetchMessages(),
            fetchConnections()
          ]);

          // Log any failures without blocking
          results.forEach((result, index) => {
            if (result.status === 'rejected') {
              const operation = ['connection requests', 'messages', 'connections'][index];
              console.error(`Failed to load ${operation}:`, result.reason);
            }
          });
        } finally {
          setLoading(false);
        }
      };

      loadData();

      // Listen for message updates from other hooks
      const handleMessagesUpdate = () => {
        console.log('🔄 Networking messages update detected, reloading...');
        globalCache.invalidate(`messages_${user.id}`);
        fetchMessages(true);
        setRefreshTrigger(prev => prev + 1); // Force all components to recalculate
      };

      window.addEventListener('networking-messages-updated', handleMessagesUpdate);

      // 🔥 PHASE 1: Remplacé Realtime par polling (30s connection_requests, 1min messages)
      import('@/utils/realtimeOptimizer').then(({ createPollingInterval }) => {
        const stopConnectionPolling = createPollingInterval(
          'connection_requests',
          async () => {
            globalCache.invalidate(`connection_requests_${user.id}`);
            await fetchConnectionRequests();
          },
          true
        );

        const stopMessagePolling = createPollingInterval(
          'messages',
          async () => {
            console.log('📊 Polling messages...');
            globalCache.invalidate(`messages_${user.id}`);
            await fetchMessages();
          },
          true
        );
      });

      return () => {
        window.removeEventListener('networking-messages-updated', handleMessagesUpdate);
        
        // Clear pending timers
        if (fetchMessagesTimerRef.current) {
          clearTimeout(fetchMessagesTimerRef.current);
        }
      };
    }
  }, [user, fetchConnectionRequests, fetchMessages, fetchConnections]);

  return {
    connectionRequests,
    messages,
    connections,
    loading,
    networkingStats,
    sendConnectionRequest,
    respondToConnectionRequest,
    sendMessage,
    markMessageAsRead,
    deleteMessage,
    deleteConversation,
    fetchConnectionRequests,
    fetchMessages,
    fetchConnections
  };
};